import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { getUserPrivileges, getUserRole, isAuthenticated } from '../../../../utils/auth';
import { PRODUCT_MASTER_PRIVILEGES } from '../../../../utils/productMasterPrivileges';
import Sidebar from '../../../../components/Sidebar/sidebar';
import Header from '../../../../components/Header';
import Pagination from '../../../../components/Pagination/Pagination';
import './policyMaster.css';
import api from '../../../../utils/api';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
    ArchiveRestore,
    Ban,
    BookOpenCheck,
    CalendarClock,
    ChevronDown,
    Download,
    Edit,
    FileSpreadsheet,
    FolderTree,
    Percent,
    Plus,
    RefreshCw,
    Search,
    Upload,
    X
} from 'lucide-react';
import { API_BASE_URL } from '../../../../utils/apiBase';
import { toast } from '../../../../components/notifications/notificationService';

const emptyForm = {
    policyName: '',
    policyShortName: '',
    classOfPolicyId: '',
    policyToBeRenewed: '',
    defaultFirstFollowupDays: '',
    defaultGST: '',
    defaultBrokeragePercentage: '',
    defaultPayInBasedOn: ''
};

const PolicyMaster = () => {
    const privileges = getUserPrivileges();
    const isAdmin = getUserRole() === 'admin';
    const hasPrivilege = (key) => isAdmin || privileges.includes(key);
    const policyPrivileges = PRODUCT_MASTER_PRIVILEGES.policy;
    const [collapsed, setCollapsed] = useState(false);
    const [authenticated, setAuthenticated] = useState(false);
    const [checkingAuth, setCheckingAuth] = useState(true);
    const [showPopup, setShowPopup] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentPolicyId, setCurrentPolicyId] = useState(null);
    const [formData, setFormData] = useState(emptyForm);
    const [classOfPolicies, setClassOfPolicies] = useState([]);
    const [policies, setPolicies] = useState([]);
    const [inactiveList, setInactiveList] = useState([]);
    const [showInactivePopup, setShowInactivePopup] = useState(false);
    const [activeSearchText, setActiveSearchText] = useState('');
    const [activeClassFilter, setActiveClassFilter] = useState('');
    const [activeRenewalFilter, setActiveRenewalFilter] = useState('');
    const [inactiveSearchText, setInactiveSearchText] = useState('');
    const [inactiveClassFilter, setInactiveClassFilter] = useState('');
    const [sortField, setSortField] = useState('policyName');
    const [sortOrder, setSortOrder] = useState('asc');
    const [activePage, setActivePage] = useState(1);
    const [activeItemsPerPage, setActiveItemsPerPage] = useState(10);
    const [inactivePage, setInactivePage] = useState(1);
    const [inactiveItemsPerPage, setInactiveItemsPerPage] = useState(10);
    const [confirmDeletePopup, setConfirmDeletePopup] = useState(false);
    const [deletePolicyId, setDeletePolicyId] = useState(null);
    const [bulkErrors, setBulkErrors] = useState([]);
    const [showBulkErrorPopup, setShowBulkErrorPopup] = useState(false);
    const [showExportMenu, setShowExportMenu] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({
        defaultFirstFollowupDays: '',
        defaultGST: '',
        defaultBrokeragePercentage: ''
    });
    useEffect(() => {
        const auth = isAuthenticated();
        setAuthenticated(auth);
        setCheckingAuth(false);
    }, []);

    const showNotification = useCallback((message, type) => {
        (toast[type] || toast.info)(message);
    }, []);

    const fetchData = useCallback(async () => {
        try {
            const [classRes, policyRes] = await Promise.all([
                api.get(`${API_BASE_URL}/api/policies/classes`),
                api.get(`${API_BASE_URL}/api/policies`)
            ]);

            setClassOfPolicies(classRes.data || []);
            setPolicies(policyRes.data || []);
        } catch (error) {
            showNotification('Error fetching policy master data', 'error');
        }
    }, [showNotification]);

    useEffect(() => {
        if (!authenticated) return;
        fetchData();
    }, [authenticated, fetchData]);

    const reloadActivePolicies = useCallback(async () => {
        try {
            const res = await api.get(`${API_BASE_URL}/api/policies`);
            setPolicies(res.data || []);
        } catch {
            showNotification('Error refreshing policies', 'error');
        }
    }, [showNotification]);

    const getClassName = useCallback((policy) => {
        const cls = policy.classOfPolicyId;
        if (cls && typeof cls === 'object') return cls.classOfPolicyName || '-';
        const found = classOfPolicies.find((item) => item._id === cls);
        return found?.classOfPolicyName || '-';
    }, [classOfPolicies]);

    const getPolicyType = useCallback((policy) => {
        const cls = policy.classOfPolicyId;
        if (cls && typeof cls === 'object') return cls.policyType || '-';
        const found = classOfPolicies.find((item) => item._id === cls);
        return found?.policyType || '-';
    }, [classOfPolicies]);

    const getCategoryName = useCallback((policy) => {
        const cls = policy.classOfPolicyId;
        if (cls && typeof cls === 'object') return cls.categoryId?.categoryName || '-';
        const found = classOfPolicies.find((item) => item._id === cls);
        return found?.categoryId?.categoryName || '-';
    }, [classOfPolicies]);

    const filteredActive = useMemo(() => {
        const text = activeSearchText.trim().toLowerCase();
        return policies.filter((policy) => {
            const searchable = [
                policy.policyName,
                policy.policyShortName,
                policy.policyToBeRenewed,
                policy.defaultPayInBasedOn,
                getClassName(policy),
                getPolicyType(policy),
                getCategoryName(policy)
            ].join(' ').toLowerCase();

            const classId = typeof policy.classOfPolicyId === 'object'
                ? policy.classOfPolicyId?._id
                : policy.classOfPolicyId;

            return (
                (!text || searchable.includes(text)) &&
                (!activeClassFilter || classId === activeClassFilter) &&
                (!activeRenewalFilter || policy.policyToBeRenewed === activeRenewalFilter)
            );
        });
    }, [policies, activeSearchText, activeClassFilter, activeRenewalFilter, getClassName, getPolicyType, getCategoryName]);

    const filteredInactive = useMemo(() => {
        const text = inactiveSearchText.trim().toLowerCase();
        return inactiveList
            .filter((policy) => {
                const classId = typeof policy.classOfPolicyId === 'object'
                    ? policy.classOfPolicyId?._id
                    : policy.classOfPolicyId;
                const searchable = [
                    policy.policyName,
                    policy.policyShortName,
                    getClassName(policy),
                    getCategoryName(policy)
                ].join(' ').toLowerCase();

                return (!text || searchable.includes(text)) && (!inactiveClassFilter || classId === inactiveClassFilter);
            })
            .sort((a, b) => {
                const valA = sortField === 'className'
                    ? getClassName(a)
                    : sortField === 'categoryName'
                        ? getCategoryName(a)
                        : (a[sortField] || '').toString();
                const valB = sortField === 'className'
                    ? getClassName(b)
                    : sortField === 'categoryName'
                        ? getCategoryName(b)
                        : (b[sortField] || '').toString();

                return sortOrder === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
            });
    }, [inactiveList, inactiveSearchText, inactiveClassFilter, sortField, sortOrder, getClassName, getCategoryName]);

    const activeLast = activePage * activeItemsPerPage;
    const activeFirst = activeLast - activeItemsPerPage;
    const currentPolicies = filteredActive.slice(activeFirst, activeLast);

    const inactiveLast = inactivePage * inactiveItemsPerPage;
    const inactiveFirst = inactiveLast - inactiveItemsPerPage;
    const inactivePolicies = filteredInactive.slice(inactiveFirst, inactiveLast);

    useEffect(() => {
        setActivePage(1);
    }, [activeSearchText, activeClassFilter, activeRenewalFilter]);

    useEffect(() => {
        setInactivePage(1);
    }, [inactiveSearchText, inactiveClassFilter, sortField, sortOrder]);

    const summaryCards = [
        { label: 'Active Policies', value: policies.length, note: 'Available for policy issuance', icon: BookOpenCheck, tone: 'is-blue' },
        { label: 'Policy Classes', value: classOfPolicies.length, note: 'Mapped master classes', icon: FolderTree, tone: 'is-green' },
        { label: 'Renewable Policies', value: policies.filter((item) => item.policyToBeRenewed === 'Renewed').length, note: 'Renewal enabled', icon: RefreshCw, tone: 'is-amber' },
        { label: 'Rule Based Pay In', value: policies.filter((item) => item.defaultPayInBasedOn === 'Rule Based').length, note: 'Commission rule driven', icon: Percent, tone: 'is-teal' },
        { label: 'Inactive Policies', value: inactiveList.length, note: 'Loaded from inactive list', icon: ArchiveRestore, tone: 'is-indigo' }
    ];

    const resetForm = () => {
        setFormData(emptyForm);
        setFieldErrors({
            defaultFirstFollowupDays: '',
            defaultGST: '',
            defaultBrokeragePercentage: ''
        });
        setEditMode(false);
        setCurrentPolicyId(null);
        setShowPopup(false);
    };

    const openAddPopup = () => {
        resetForm();
        setShowPopup(true);
    };

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));

        if (field === 'defaultFirstFollowupDays') {
            setFieldErrors((prev) => ({
                ...prev,
                defaultFirstFollowupDays: value && Number(value) > 60 ? 'Followup days cannot exceed 60 days.' : ''
            }));
        }

        if (field === 'defaultGST') {
            setFieldErrors((prev) => ({
                ...prev,
                defaultGST: value && Number(value) > 100 ? 'GST cannot exceed 100%.' : ''
            }));
        }

        if (field === 'defaultBrokeragePercentage') {
            setFieldErrors((prev) => ({
                ...prev,
                defaultBrokeragePercentage: value && Number(value) > 100 ? 'Brokerage cannot exceed 100%.' : ''
            }));
        }
    };

    const isSubmitDisabled = Boolean(
        fieldErrors.defaultFirstFollowupDays ||
        fieldErrors.defaultGST ||
        fieldErrors.defaultBrokeragePercentage ||
        !formData.policyName.trim() ||
        !formData.policyShortName.trim() ||
        !formData.classOfPolicyId ||
        !formData.policyToBeRenewed ||
        !formData.defaultGST.toString().trim() ||
        !formData.defaultBrokeragePercentage.toString().trim() ||
        !formData.defaultPayInBasedOn
    );

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        if (isSubmitDisabled) {
            showNotification('Please complete all required fields', 'error');
            return;
        }

        const payload = {
            ...formData,
            defaultFirstFollowupDays: formData.defaultFirstFollowupDays || 0
        };

        try {
            if (editMode) {
                const res = await api.put(`${API_BASE_URL}/api/policies/${currentPolicyId}`, payload);
                setPolicies((prev) => prev.map((policy) => (policy._id === currentPolicyId ? res.data.policy : policy)));
                showNotification(res.data.message || 'Policy updated successfully', 'success');
            } else {
                const res = await api.post(`${API_BASE_URL}/api/policies`, payload);
                setPolicies((prev) => [...prev, res.data.policy]);
                showNotification(res.data.message || 'Policy added successfully', 'success');
            }

            resetForm();
        } catch (error) {
            const msg = error.response?.data?.message || 'Error saving policy';
            showNotification(msg, 'error');
        }
    };

    const handleEditPolicy = (policy) => {
        const classId = typeof policy.classOfPolicyId === 'object'
            ? policy.classOfPolicyId?._id
            : policy.classOfPolicyId;

        setFormData({
            policyName: policy.policyName || '',
            policyShortName: policy.policyShortName || '',
            classOfPolicyId: classId || '',
            policyToBeRenewed: policy.policyToBeRenewed || '',
            defaultFirstFollowupDays: policy.defaultFirstFollowupDays || '',
            defaultGST: policy.defaultGST || '',
            defaultBrokeragePercentage: policy.defaultBrokeragePercentage || '',
            defaultPayInBasedOn: policy.defaultPayInBasedOn || ''
        });
        setFieldErrors({
            defaultFirstFollowupDays: '',
            defaultGST: '',
            defaultBrokeragePercentage: ''
        });
        setCurrentPolicyId(policy._id);
        setEditMode(true);
        setShowPopup(true);
    };

    const handleDeletePolicy = (policy) => {
        setDeletePolicyId(policy._id);
        setConfirmDeletePopup(true);
    };

    const confirmDelete = async () => {
        try {
            await api.put(`${API_BASE_URL}/api/policies/disable/${deletePolicyId}`);
            setPolicies((prev) => prev.filter((policy) => policy._id !== deletePolicyId));
            showNotification('Policy moved to inactive list', 'success');
        } catch (error) {
            const msg = error.response?.data?.message || 'Error deactivating policy';
            showNotification(msg, 'error');
        } finally {
            setConfirmDeletePopup(false);
            setDeletePolicyId(null);
        }
    };

    const openInactivePopup = async () => {
        try {
            const res = await api.get(`${API_BASE_URL}/api/policies/inactive/list`);
            setInactiveList(res.data || []);
            setInactiveSearchText('');
            setInactiveClassFilter('');
            setSortField('policyName');
            setSortOrder('asc');
            setInactivePage(1);
            setShowInactivePopup(true);
        } catch (error) {
            showNotification('Error loading inactive policies', 'error');
        }
    };

    const handleBulkUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const uploadData = new FormData();
        uploadData.append('file', file);

        try {
            const res = await api.post(
                `${API_BASE_URL}/api/policies/bulk-upload`,
                uploadData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            const { inserted, failed, errors } = res.data;

            if (inserted > 0 && failed === 0) {
                showNotification(`Bulk upload completed successfully. ${inserted} policies uploaded.`, 'success');
            } else if (inserted > 0 && failed > 0) {
                showNotification(`Bulk upload completed with warnings. ${inserted} uploaded, ${failed} failed.`, 'warning');
                setBulkErrors(errors || []);
                setShowBulkErrorPopup(true);
            } else {
                showNotification('Bulk upload failed. No records were uploaded.', 'error');
            }

            await reloadActivePolicies();
        } catch (error) {
            showNotification(error.response?.data?.message || 'Bulk upload failed', 'error');
        }

        e.target.value = null;
    };

    const downloadBulkTemplate = async () => {
        try {
            const response = await api.get(`${API_BASE_URL}/api/policies/bulk-template`, {
                responseType: 'blob'
            });
            saveAs(response.data, 'Policy_Bulk_Upload_Template.xlsx');
        } catch (error) {
            showNotification('Unable to download policy template', 'error');
        }
    };

    const activatePolicy = async (id) => {
        try {
            await api.put(`${API_BASE_URL}/api/policies/activate/${id}`);
            await reloadActivePolicies();
            setInactiveList((prev) => prev.filter((item) => item._id !== id));
            showNotification('Policy activated successfully', 'success');
        } catch (error) {
            showNotification('Error activating policy', 'error');
        }
    };

    const restoreAll = async () => {
        try {
            await api.put(`${API_BASE_URL}/api/policies/restore-all`);
            await reloadActivePolicies();
            setInactiveList([]);
            showNotification('All inactive policies restored successfully', 'success');
        } catch (error) {
            showNotification('Error restoring policies', 'error');
        }
    };

    const getExportRows = (rows = filteredActive) =>
        rows.map((policy, index) => ({
            'S.No': index + 1,
            'Policy Name': policy.policyName || '',
            'Short Name': policy.policyShortName || '',
            'Class of Policy': getClassName(policy),
            'Policy Type': getPolicyType(policy),
            Category: getCategoryName(policy),
            'Renewal Status': policy.policyToBeRenewed || '',
            'First Followup Days': policy.defaultFirstFollowupDays ?? '',
            'Default GST %': policy.defaultGST ?? '',
            'Brokerage %': policy.defaultBrokeragePercentage ?? '',
            'Pay In Based On': policy.defaultPayInBasedOn || ''
        }));

    const handleEmptyExport = () => {
        if (!filteredActive.length) {
            showNotification('No policy records available to download', 'error');
            return true;
        }
        return false;
    };

    const exportPoliciesExcel = () => {
        if (handleEmptyExport()) return;
        const worksheet = XLSX.utils.json_to_sheet(getExportRows());
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Policy Master');
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([excelBuffer], { type: 'application/octet-stream' }), 'policy-master.xlsx');
        setShowExportMenu(false);
    };

    const exportPoliciesCsv = () => {
        if (handleEmptyExport()) return;
        const worksheet = XLSX.utils.json_to_sheet(getExportRows());
        const csv = XLSX.utils.sheet_to_csv(worksheet);
        saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), 'policy-master.csv');
        setShowExportMenu(false);
    };

    const escapeHtml = (value) =>
        String(value ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');

    const exportPoliciesPdf = () => {
        if (handleEmptyExport()) return;
        const rows = getExportRows();
        const printWindow = window.open('', '_blank', 'noopener,noreferrer');
        if (!printWindow) {
            showNotification('Please allow popups to export PDF.', 'error');
            return;
        }

        const tableRows = rows.map((row) => `
            <tr>
                <td>${escapeHtml(row['S.No'])}</td>
                <td>${escapeHtml(row['Policy Name'])}</td>
                <td>${escapeHtml(row['Short Name'])}</td>
                <td>${escapeHtml(row['Class of Policy'])}</td>
                <td>${escapeHtml(row['Policy Type'])}</td>
                <td>${escapeHtml(row.Category)}</td>
                <td>${escapeHtml(row['Renewal Status'])}</td>
                <td>${escapeHtml(row['Default GST %'])}</td>
                <td>${escapeHtml(row['Brokerage %'])}</td>
                <td>${escapeHtml(row['Pay In Based On'])}</td>
            </tr>
        `).join('');

        printWindow.document.write(`
            <!doctype html>
            <html>
              <head>
                <title>Policy Master Export</title>
                <style>
                  body { font-family: Arial, sans-serif; color: #111827; margin: 24px; }
                  h1 { margin: 0; font-size: 22px; }
                  p { margin: 6px 0 18px; color: #667085; font-size: 12px; }
                  table { width: 100%; border-collapse: collapse; font-size: 10px; }
                  th, td { border: 1px solid #d7e0ea; padding: 7px; text-align: left; vertical-align: top; }
                  th { background: #f1f5f9; text-transform: uppercase; font-size: 9px; }
                  tr:nth-child(even) td { background: #fbfdff; }
                  @media print { body { margin: 10mm; } }
                </style>
              </head>
              <body>
                <h1>Policy Master</h1>
                <p>${rows.length} policy record(s) exported on ${new Date().toLocaleString('en-IN')}</p>
                <table>
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th>Policy Name</th>
                      <th>Short Name</th>
                      <th>Class</th>
                      <th>Type</th>
                      <th>Category</th>
                      <th>Renewal</th>
                      <th>GST</th>
                      <th>Brokerage</th>
                      <th>Pay In</th>
                    </tr>
                  </thead>
                  <tbody>${tableRows || '<tr><td colspan="10">No policy records found.</td></tr>'}</tbody>
                </table>
                <script>
                  window.onload = function () {
                    window.print();
                  };
                </script>
              </body>
            </html>
        `);
        printWindow.document.close();
        setShowExportMenu(false);
    };

    if (checkingAuth) {
        return <div className="policy-loading-screen">Loading...</div>;
    }

    if (!authenticated) {
        return <Navigate to="/" replace />;
    }

    return (
        <div className="policy-master-container">
            <div className="sidebar">
                <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
            </div>

            <div className={`policy-master-content-shell ${collapsed ? 'collapsed' : ''}`}>
                <div className="policy-master-sticky-header">
                    <Header />
                </div>

                <main className="policy-master-scroll-area">
                    <section className="policy-master-content">
                        <div className="policy-page-hero">
                            <div>
                                <span className="policy-page-eyebrow">System Settings / Product Master</span>
                                <h1>Policy Master</h1>
                                <p>Manage product policies, policy classes, renewal rules, GST, brokerage, and pay-in configuration from one workspace.</p>
                            </div>
                            <div className="policy-hero-stats">
                                <span>Active Policies</span>
                                <strong>{policies.length}</strong>
                                <small>{filteredActive.length} matching current filters</small>
                            </div>
                        </div>

                        <div className="policy-summary-grid">
                            {summaryCards.map(({ label, value, note, icon: Icon, tone }) => (
                                <article className={`policy-summary-card ${tone}`} key={label}>
                                    <Icon size={22} />
                                    <span>{label}</span>
                                    <strong>{value}</strong>
                                    <small>{note}</small>
                                </article>
                            ))}
                        </div>

                        <section className="policy-filter-card">
                            <div className="policy-filter-header">
                                <div>
                                    <h2>Search & Filters</h2>
                                    <p>Find policies by name, short code, class, category, renewal status, or pay-in setup.</p>
                                </div>
                                <span className="policy-result-pill">{filteredActive.length} results</span>
                            </div>

                            <div className="policy-filter-grid">
                                <label className="policy-search-field">
                                    <span>Search Policies</span>
                                    <div>
                                        <Search size={17} />
                                        <input
                                            type="text"
                                            placeholder="Policy name, short name, class, category"
                                            value={activeSearchText}
                                            onChange={(e) => setActiveSearchText(e.target.value)}
                                        />
                                    </div>
                                </label>

                                <label>
                                    <span>Class of Policy</span>
                                    <select value={activeClassFilter} onChange={(e) => setActiveClassFilter(e.target.value)}>
                                        <option value="">All Classes</option>
                                        {classOfPolicies.map((cls) => (
                                            <option key={cls._id} value={cls._id}>
                                                {cls.classOfPolicyName}
                                            </option>
                                        ))}
                                    </select>
                                </label>

                                <label>
                                    <span>Renewal Status</span>
                                    <select value={activeRenewalFilter} onChange={(e) => setActiveRenewalFilter(e.target.value)}>
                                        <option value="">All Status</option>
                                        <option value="Renewed">Renewed</option>
                                        <option value="Not Renewed">Not Renewed</option>
                                    </select>
                                </label>
                            </div>

                            <div className="policy-action-row">
                                {hasPrivilege(policyPrivileges.add) && <button className="policy-btn policy-btn-primary" onClick={openAddPopup}>
                                    <Plus size={16} /> Add Policy
                                </button>}

                                {hasPrivilege(policyPrivileges.bulkUpload) && <button
                                    className="policy-btn policy-btn-success"
                                    onClick={() => document.getElementById('policyBulkUploadInput').click()}
                                >
                                    <Upload size={16} /> Bulk Upload
                                </button>}
                                <input
                                    type="file"
                                    id="policyBulkUploadInput"
                                    accept=".xlsx,.xls"
                                    hidden
                                    onChange={handleBulkUpload}
                                />

                                {hasPrivilege(policyPrivileges.bulkUpload) && <button
                                    className="policy-btn policy-btn-light"
                                    onClick={downloadBulkTemplate}
                                >
                                    <FileSpreadsheet size={16} /> Template
                                </button>}

                                {hasPrivilege(policyPrivileges.viewInactive) && <button className="policy-btn policy-btn-ghost" onClick={openInactivePopup}>
                                    <ArchiveRestore size={16} /> Inactive List
                                </button>}

                                <button
                                    className="policy-btn policy-btn-ghost"
                                    onClick={() => {
                                        setActiveSearchText('');
                                        setActiveClassFilter('');
                                        setActiveRenewalFilter('');
                                    }}
                                >
                                    <RefreshCw size={16} /> Reset
                                </button>

                                {hasPrivilege(policyPrivileges.download) && <div className="policy-export-menu">
                                    <button
                                        className="policy-btn policy-btn-export"
                                        onClick={() => setShowExportMenu((prev) => !prev)}
                                    >
                                        <Download size={16} /> Download <ChevronDown size={15} />
                                    </button>
                                    {showExportMenu && (
                                        <div className="policy-export-dropdown">
                                            <button onClick={exportPoliciesCsv}>CSV</button>
                                            <button onClick={exportPoliciesExcel}>Excel</button>
                                            <button onClick={exportPoliciesPdf}>PDF</button>
                                        </div>
                                    )}
                                </div>}
                            </div>
                        </section>

                        <section className="policy-table-card">
                            <div className="policy-table-toolbar">
                                <div>
                                    <h2>Policy Records</h2>
                                    <p>Sticky headers, structured product details, and quick row actions.</p>
                                </div>
                                <span className="policy-result-pill">{currentPolicies.length} shown</span>
                            </div>

                            <div className="policy-table-scroll">
                                <table className="policy-table">
                                    <thead>
                                        <tr>
                                            <th className="policy-sticky-name">Policy</th>
                                            <th>Class</th>
                                            <th>Category</th>
                                            <th>Renewal</th>
                                            <th>Followup</th>
                                            <th>GST</th>
                                            <th>Brokerage</th>
                                            <th>Pay In</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentPolicies.length ? (
                                            currentPolicies.map((policy) => (
                                                <tr key={policy._id}>
                                                    <td className="policy-sticky-name" data-label="Policy">
                                                        <div className="policy-name-cell">
                                                            <span className="policy-avatar">
                                                                <BookOpenCheck size={17} />
                                                            </span>
                                                            <span>
                                                                <strong>{policy.policyName}</strong>
                                                                <small>{policy.policyShortName || 'No short name'}</small>
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td data-label="Class">
                                                        <span className="policy-chip">{getClassName(policy)}</span>
                                                        <small className="policy-muted-line">{getPolicyType(policy)}</small>
                                                    </td>
                                                    <td data-label="Category">{getCategoryName(policy)}</td>
                                                    <td data-label="Renewal">
                                                        <span className={`policy-status ${policy.policyToBeRenewed === 'Renewed' ? 'is-active' : ''}`}>
                                                            {policy.policyToBeRenewed || '-'}
                                                        </span>
                                                    </td>
                                                    <td data-label="Followup">
                                                        <span className="policy-icon-text">
                                                            <CalendarClock size={15} />
                                                            {policy.defaultFirstFollowupDays || 0} days
                                                        </span>
                                                    </td>
                                                    <td data-label="GST">{policy.defaultGST ?? '-'}%</td>
                                                    <td data-label="Brokerage">{policy.defaultBrokeragePercentage ?? '-'}%</td>
                                                    <td data-label="Pay In">{policy.defaultPayInBasedOn || '-'}</td>
                                                    <td data-label="Actions">
                                                        <div className="policy-action-icons">
                                                            {hasPrivilege(policyPrivileges.edit) && <button className="policy-icon-btn edit-btn" onClick={() => handleEditPolicy(policy)} title="Edit Policy" aria-label={`Edit ${policy.policyName}`}>
                                                                <Edit size={18} />
                                                            </button>}
                                                            {hasPrivilege(policyPrivileges.deactivate) && <button className="policy-icon-btn delete-btn" onClick={() => handleDeletePolicy(policy)} title="Deactivate Policy" aria-label={`Deactivate ${policy.policyName}`}>
                                                                <Ban size={18} />
                                                            </button>}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="9">
                                                    <div className="policy-empty-state">
                                                        <BookOpenCheck size={42} />
                                                        <h3>No Policies Found</h3>
                                                        <p>Try changing the filters or add a new policy.</p>
                                                        {hasPrivilege(policyPrivileges.add) && <button className="policy-btn policy-btn-primary" onClick={openAddPopup}>
                                                            <Plus size={16} /> Add Policy
                                                        </button>}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </section>

                        <div className="policy-pagination-row">
                            <div className="policy-pagination-meta">
                                <span>
                                    Showing {filteredActive.length ? activeFirst + 1 : 0}-{Math.min(activeLast, filteredActive.length)} of {filteredActive.length}
                                </span>
                                <small>Filtered policy records</small>
                            </div>

                            <div className="policy-pagination-controls">
                                <Pagination
                                    totalItems={filteredActive.length}
                                    itemsPerPage={activeItemsPerPage}
                                    currentPage={activePage}
                                    onPageChange={setActivePage}
                                />
                            </div>

                            <div className="policy-items-per-page">
                                <label>Items per page:</label>
                                <select
                                    value={activeItemsPerPage}
                                    onChange={(e) => {
                                        setActiveItemsPerPage(Number(e.target.value));
                                        setActivePage(1);
                                    }}
                                >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                        </div>

                        {showPopup && (
                            <div className="policy-popup-overlay">
                                <div className="policy-popup">
                                    <button className="policy-popup-close" onClick={resetForm} aria-label="Close policy form">
                                        <X size={18} />
                                    </button>
                                    <h2>{editMode ? 'Edit Policy' : 'Add Policy'}</h2>
                                    <p className="policy-popup-subtitle">Configure policy identity, renewal behavior, tax, brokerage, and pay-in rules.</p>

                                    <form onSubmit={handleFormSubmit} className="policy-form">
                                        <label>
                                            <span>Policy Name</span>
                                            <input
                                                type="text"
                                                placeholder="Enter policy name"
                                                value={formData.policyName}
                                                onChange={(e) => handleInputChange('policyName', e.target.value)}
                                                required
                                            />
                                        </label>

                                        <label>
                                            <span>Policy Short Name</span>
                                            <input
                                                type="text"
                                                placeholder="Short display name"
                                                value={formData.policyShortName}
                                                onChange={(e) => handleInputChange('policyShortName', e.target.value)}
                                                required
                                            />
                                        </label>

                                        <label>
                                            <span>Class of Policy</span>
                                            <select
                                                value={formData.classOfPolicyId}
                                                onChange={(e) => handleInputChange('classOfPolicyId', e.target.value)}
                                                required
                                            >
                                                <option value="">Select Class of Policy</option>
                                                {classOfPolicies.map((cls) => (
                                                    <option key={cls._id} value={cls._id}>
                                                        {cls.classOfPolicyName}{cls.policyType ? `, ${cls.policyType}` : ''}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>

                                        <label>
                                            <span>Policy To Be Renewed</span>
                                            <select
                                                value={formData.policyToBeRenewed}
                                                onChange={(e) => handleInputChange('policyToBeRenewed', e.target.value)}
                                                required
                                            >
                                                <option value="">Select Renewal Status</option>
                                                <option value="Renewed">Renewed</option>
                                                <option value="Not Renewed">Not Renewed</option>
                                            </select>
                                        </label>

                                        <label>
                                            <span>Default First Followup Days</span>
                                            <input
                                                type="number"
                                                placeholder="Example: 30"
                                                value={formData.defaultFirstFollowupDays}
                                                onChange={(e) => handleInputChange('defaultFirstFollowupDays', e.target.value)}
                                            />
                                            {fieldErrors.defaultFirstFollowupDays && <small className="policy-error-text">{fieldErrors.defaultFirstFollowupDays}</small>}
                                        </label>

                                        <label>
                                            <span>Default GST %</span>
                                            <input
                                                type="number"
                                                placeholder="Example: 18"
                                                value={formData.defaultGST}
                                                onChange={(e) => handleInputChange('defaultGST', e.target.value)}
                                                required
                                            />
                                            {fieldErrors.defaultGST && <small className="policy-error-text">{fieldErrors.defaultGST}</small>}
                                        </label>

                                        <label>
                                            <span>Default Brokerage Percentage</span>
                                            <input
                                                type="number"
                                                placeholder="Example: 15"
                                                value={formData.defaultBrokeragePercentage}
                                                onChange={(e) => handleInputChange('defaultBrokeragePercentage', e.target.value)}
                                                required
                                            />
                                            {fieldErrors.defaultBrokeragePercentage && <small className="policy-error-text">{fieldErrors.defaultBrokeragePercentage}</small>}
                                        </label>

                                        <label>
                                            <span>Default Pay In Based On</span>
                                            <select
                                                value={formData.defaultPayInBasedOn}
                                                onChange={(e) => handleInputChange('defaultPayInBasedOn', e.target.value)}
                                                required
                                            >
                                                <option value="">Select Pay In Type</option>
                                                <option value="Rule Based">Rule Based</option>
                                                <option value="Fixed">Fixed</option>
                                            </select>
                                        </label>

                                        <div className="policy-popup-actions">
                                            <button type="submit" className="policy-submit-btn" disabled={isSubmitDisabled}>
                                                {editMode ? 'Update Policy' : 'Save Policy'}
                                            </button>
                                            <button type="button" className="policy-popup-cancel-btn" onClick={resetForm}>
                                                Cancel
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        )}

                        {showInactivePopup && (
                            <div className="policy-popup-overlay">
                                <div className="policy-popup large">
                                    <button className="policy-popup-close" onClick={() => setShowInactivePopup(false)} aria-label="Close inactive policies">
                                        <X size={18} />
                                    </button>

                                    <h2>Inactive Policies</h2>
                                    <p className="policy-popup-subtitle">Review deactivated policies and restore records when required.</p>

                                    <div className="policy-inactive-filters">
                                        <input
                                            type="text"
                                            placeholder="Search inactive policies"
                                            value={inactiveSearchText}
                                            onChange={(e) => setInactiveSearchText(e.target.value)}
                                        />

                                        <select value={inactiveClassFilter} onChange={(e) => setInactiveClassFilter(e.target.value)}>
                                            <option value="">All Classes</option>
                                            {classOfPolicies.map((cls) => (
                                                <option key={cls._id} value={cls._id}>
                                                    {cls.classOfPolicyName}
                                                </option>
                                            ))}
                                        </select>

                                        <select value={sortField} onChange={(e) => setSortField(e.target.value)}>
                                            <option value="policyName">Policy Name</option>
                                            <option value="policyShortName">Short Name</option>
                                            <option value="className">Class Name</option>
                                            <option value="categoryName">Category</option>
                                        </select>

                                        <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
                                            <option value="asc">ASC</option>
                                            <option value="desc">DESC</option>
                                        </select>

                                        <button className="policy-restore-all-btn" onClick={restoreAll}>
                                            <ArchiveRestore size={15} /> Restore All
                                        </button>
                                    </div>

                                    <div className="policy-inactive-table-scroll">
                                        <table className="policy-inactive-table">
                                            <thead>
                                                <tr>
                                                    <th>Policy</th>
                                                    <th>Class</th>
                                                    <th>Category</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {inactivePolicies.length ? (
                                                    inactivePolicies.map((item) => (
                                                        <tr key={item._id}>
                                                            <td>{item.policyName}</td>
                                                            <td>{getClassName(item)}</td>
                                                            <td>{getCategoryName(item)}</td>
                                                            <td>
                                                                <button className="policy-activate-btn" onClick={() => activatePolicy(item._id)}>
                                                                    Restore
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="4">No inactive policies found.</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="policy-modal-pagination-row">
                                        <Pagination
                                            totalItems={filteredInactive.length}
                                            itemsPerPage={inactiveItemsPerPage}
                                            currentPage={inactivePage}
                                            onPageChange={setInactivePage}
                                        />

                                        <div className="policy-items-per-page">
                                            <label>Items per page:</label>
                                            <select
                                                value={inactiveItemsPerPage}
                                                onChange={(e) => {
                                                    setInactiveItemsPerPage(Number(e.target.value));
                                                    setInactivePage(1);
                                                }}
                                            >
                                                <option value={5}>5</option>
                                                <option value={10}>10</option>
                                                <option value={20}>20</option>
                                                <option value={50}>50</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {confirmDeletePopup && (
                            <div className="policy-confirmation-modal-overlay">
                                <div className="policy-confirmation-modal-content">
                                    <h3>Confirm Deactivation</h3>
                                    <p>Are you sure you want to deactivate this policy?</p>

                                    <div className="policy-confirmation-modal-actions">
                                        <button className="policy-confirm-btn" onClick={confirmDelete}>Yes</button>
                                        <button
                                            className="policy-cancel-btn"
                                            onClick={() => {
                                                setConfirmDeletePopup(false);
                                                setDeletePolicyId(null);
                                            }}
                                        >
                                            No
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {showBulkErrorPopup && (
                            <div className="policy-popup-overlay">
                                <div className="policy-popup large">
                                    <button className="policy-popup-close" onClick={() => setShowBulkErrorPopup(false)} aria-label="Close bulk upload errors">
                                        <X size={18} />
                                    </button>

                                    <h2>Bulk Upload Errors</h2>
                                    <p className="policy-popup-subtitle">Rows below were skipped during import.</p>

                                    <div className="policy-inactive-table-scroll">
                                        <table className="policy-inactive-table">
                                            <thead>
                                                <tr>
                                                    <th>Excel Row</th>
                                                    <th>Error Reason</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {bulkErrors.map((err, index) => (
                                                    <tr key={index}>
                                                        <td>{err.row}</td>
                                                        <td>{err.reason}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}
                    </section>
                </main>
            </div>
        </div>
    );
};

export default PolicyMaster;
